import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  alpha,
  CircularProgress,
  Card,
  IconButton,
  Tooltip,
  Badge,
  Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AutorenewOutlined as RetryIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  ArrowForward as ArrowIcon,
  Category as CategoryIcon,
  School as SchoolIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useSearchReadingPractices } from '../../hooks/useSearchReadingPracticeRequests';
import { readingPracticeRequestsService } from '../../../../services/request-service';
import { categories } from '../../constants/categories';

const RequestsList = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState([
    'PENDING',
    'GENERATION_FAILED',
    'DRAFTING_FAILED',
  ]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedGradeLevels, setSelectedGradeLevels] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);

  const {
    searchPractices,
    isLoading: loading,
    error,
    data: results
  } = useSearchReadingPractices();

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    searchPractices({
      term: searchTerm,
      statuses: selectedStatuses,
      categories: selectedCategories,
      pageNumber: newPage,
      pageSize: results?.pageSize || 10
    });
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    searchPractices({
      term: searchTerm,
      statuses: selectedStatuses,
      categories: selectedCategories,
      pageNumber: 0,
      pageSize: newPageSize
    });
  };

  // Handle search form submission
// Handle search form submission
const handleSearch = (e) => {
  e?.preventDefault();
  searchPractices({
    term: searchTerm,
    statuses: selectedStatuses,
    categoryIds: selectedCategories,
    gradeLevels: selectedGradeLevels,
    pageNumber: 0,
    pageSize: results?.pageSize || 10
  });
};

  // Handle status filter change
  const handleStatusChange = (event) => {
    setSelectedStatuses(event.target.value);
  };

  // Handle category filter change
  const handleCategoryChange = (event) => {
    setSelectedCategories(event.target.value);
  };
  
  // Handle grade level filter change
  const handleGradeLevelChange = (event) => {
    setSelectedGradeLevels(event.target.value);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedStatuses([]);
    setSelectedCategories([]);
    setSelectedGradeLevels([]);
  };

  // View request details
  const handleViewRequest = (id) => {
    navigate(`/requests/${id}`);
  };

  // Retry a failed request
  const handleRetryRequest = async (id, event) => {
    event.stopPropagation(); // Prevent row click
    try {
      await readingPracticeRequestsService.retryRequest(id);
      // Refresh the search results
      handleSearch();
    } catch (error) {
      console.error('Error retrying request:', error);
    }
  };

  // Get status chip props based on status
  const getStatusChipProps = (status) => {
    const statusMap = {
      'PENDING': {
        color: 'default',
        label: 'Pending',
        icon: <CircularProgress size={12} color="inherit" />,
        bg: alpha(theme.palette.grey[500], 0.2),
        textColor: theme.palette.grey[300]
      },
      'GENERATING_CONTENT': {
        color: 'primary',
        label: 'Generating',
        icon: <CircularProgress size={12} color="inherit" />,
        bg: alpha(theme.palette.primary.main, 0.1),
        textColor: theme.palette.primary.main
      },
      'GENERATED': {
        color: 'info',
        label: 'Generated',
        bg: alpha(theme.palette.info.main, 0.1),
        textColor: theme.palette.info.main
      },
      'GENERATION_FAILED': {
        color: 'error',
        label: 'Generation Failed',
        bg: alpha(theme.palette.error.main, 0.1),
        textColor: theme.palette.error.main
      },
      'DRAFTING': {
        color: 'primary',
        label: 'Drafting',
        icon: <CircularProgress size={12} color="inherit" />,
        bg: alpha(theme.palette.primary.main, 0.1),
        textColor: theme.palette.primary.main
      },
      'DRAFTING_FAILED': {
        color: 'error',
        label: 'Drafting Failed',
        bg: alpha(theme.palette.error.main, 0.1),
        textColor: theme.palette.error.main
      },
      'COMPLETED': {
        color: 'success',
        label: 'Completed',
        bg: alpha(theme.palette.success.main, 0.1),
        textColor: theme.palette.success.main
      }
    };

    return statusMap[status] || {
      color: 'default',
      label: status,
      bg: theme.palette.grey[300],
      textColor: theme.palette.text.primary
    };
  };


  // Count selected filters
  const activeFilterCount = selectedStatuses.length + selectedCategories.length + selectedGradeLevels.length + (searchTerm ? 1 : 0);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Card
        elevation={1}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
        }}
      >
        <Box 
          sx={{ 
            p: 3,
            borderBottom: '1px solid',
            borderColor: alpha(theme.palette.divider, 0.05),
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="h5" fontWeight="600">
            Reading Practice Requests
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title={filterOpen ? "Hide filters" : "Show filters"}>
              <Badge
                badgeContent={activeFilterCount || null}
                color="primary"
                overlap="circular"
              >
                <IconButton 
                  onClick={() => setFilterOpen(!filterOpen)}
                  color={filterOpen ? "primary" : "default"}
                >
                  <FilterIcon />
                </IconButton>
              </Badge>
            </Tooltip>
            
            <Tooltip title="Refresh">
              <IconButton 
                onClick={handleSearch} 
                disabled={loading}
                color="default"
              >
                {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {filterOpen && (
          <Box 
            component="form" 
            onSubmit={handleSearch} 
            sx={{ 
              p: 3, 
              backgroundColor: alpha(theme.palette.background.default, 0.4),
              borderBottom: '1px solid',
              borderColor: theme.palette.divider
            }}
          >
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search by topic..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton 
                          size="small" 
                          onClick={() => setSearchTerm('')}
                          edge="end"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Grade Level</InputLabel>
                  <Select
                    multiple
                    value={selectedGradeLevels}
                    onChange={handleGradeLevelChange}
                    label="Grade Level"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={`Grade ${value}`}
                            color="secondary"
                            size="small"
                            icon={<SchoolIcon fontSize="small" />}
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((grade) => (
                      <MenuItem key={grade} value={grade}>
                        <SchoolIcon sx={{ color: theme.palette.secondary.main, mr: 1, fontSize: 18 }} />
                        Grade {grade}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    multiple
                    value={selectedStatuses}
                    onChange={handleStatusChange}
                    label="Status"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const { color, label } = getStatusChipProps(value);
                          return (
                            <Chip
                              key={value}
                              label={label}
                              color={color}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {['PENDING', 'GENERATING_CONTENT', 'GENERATED', 'GENERATION_FAILED', 'DRAFTING', 'COMPLETED', 'DRAFTING_FAILED'].map((status) => {
                      const { label, bg, textColor } = getStatusChipProps(status);
                      return (
                        <MenuItem key={status} value={status}>
                          <Chip
                            label={label}
                            size="small"
                            sx={{ 
                              mr: 1, 
                              backgroundColor: bg,
                              color: textColor,
                              border: 'none'
                            }}
                          />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    multiple
                    value={selectedCategories}
                    onChange={handleCategoryChange}
                    label="Category"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={categories[value] || `Category ${value}`}
                            color="primary"
                            size="small"
                            variant="outlined"
                            icon={<CategoryIcon fontSize="small" />}
                          />
                        ))}
                      </Box>
                    )}
                  >
                    {categories.map((category, index) => (
                      <MenuItem key={index} value={index}>
                        <CategoryIcon sx={{ color: theme.palette.primary.main, mr: 1, fontSize: 18 }} />
                        {category || `Category ${index}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={12} md={12}>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                    sx={{ 
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: 2
                    }}
                  >
                    Search
                  </Button>

                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={handleClearFilters}
                    disabled={!activeFilterCount}
                    startIcon={<ClearIcon />}
                    sx={{ 
                      borderRadius: '8px',
                      textTransform: 'none'
                    }}
                  >
                    Clear
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        )}

        {error && (
          <Box sx={{ p: 2, backgroundColor: alpha(theme.palette.error.main, 0.05) }}>
            <Typography color="error" variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ClearIcon fontSize="small" color="error" /> {error}
            </Typography>
          </Box>
        )}

        <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Topic</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Grade Level</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && !results && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={40} />
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                      Loading requests...
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {results && results.content && results.content.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <SearchIcon sx={{ fontSize: 48, color: theme.palette.text.secondary, opacity: 0.5, mb: 2 }} />
                      <Typography variant="h6" color="textSecondary" gutterBottom>
                        No requests found
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Try adjusting your search filters
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {results && results.content && results.content.map((request) => {
                const { label, bg, textColor, icon } = getStatusChipProps(request.status);
                const isFailedStatus = request.status.includes('FAILED');
                
                return (
                  <TableRow
                    key={request.id}
                    hover
                    onClick={() => handleViewRequest(request.id)}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.03)
                      }
                    }}
                  >
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight={500}>
                        {request.topic || 'N/A'}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Chip
                        label={categories[request.categoryId] || `Category ${request.categoryId}`}
                        size="small"
                        icon={<CategoryIcon fontSize="small" />}
                        variant="outlined"
                        sx={{ 
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                          color: theme.palette.primary.main,
                          fontWeight: 400,
                          border: 'none',
                          '.MuiChip-icon': {
                            color: 'inherit'
                          }
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={label}
                        size="small"
                        icon={icon}
                        sx={{ 
                          backgroundColor: bg,
                          color: textColor,
                          fontWeight: 500,
                          border: 'none',
                          '.MuiChip-icon': {
                            color: 'inherit'
                          }
                        }}
                      />
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {request.gradeLevel || 'N/A'}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="View details">
                          <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewRequest(request.id);
                            }}
                            endIcon={<ArrowIcon fontSize="small" />}
                            sx={{ 
                              borderRadius: '8px',
                              textTransform: 'none',
                              fontWeight: 500
                            }}
                          >
                            View
                          </Button>
                        </Tooltip>

                        {isFailedStatus && (
                          <Tooltip title="Retry request">
                            <Button
                              variant="outlined"
                              color="warning"
                              size="small"
                              startIcon={<RetryIcon />}
                              onClick={(e) => handleRetryRequest(request.id, e)}
                              sx={{ 
                                borderRadius: '8px',
                                textTransform: 'none'
                              }}
                            >
                              Retry
                            </Button>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {results && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            px: 2,
            py: 1,
            borderTop: '1px solid',
            borderColor: alpha(theme.palette.divider, 0.1)
          }}>
            <Typography variant="body2" color="textSecondary">
              Showing {Math.min(results.pageSize, results.totalElements)} of {results.totalElements} results
            </Typography>
            
            <TablePagination
              component="div"
              count={results.totalElements || 0}
              page={results.pageNumber || 0}
              onPageChange={handleChangePage}
              rowsPerPage={results.pageSize || 10}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage=""
              sx={{
                '.MuiTablePagination-toolbar': {
                  paddingLeft: 1
                },
                '.MuiTablePagination-displayedRows': {
                  display: 'none'
                }
              }}
            />
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default RequestsList;